import Schema from '@data-driven-forms/react-form-renderer/dist/cjs/schema';
import { FIELD_NAMES } from '../../field-names';
import {
  DBFormSchema,
  fieldCheckboxSection,
  fieldDateSection,
  fieldRadioSection,
  fieldTextSection,
  headerSection,
  spacerSection,
  fieldSelectSection,
  fieldToggleSection,
  fieldUploadSection
} from '..';
import {
  fieldSelectLanguagesSection,
  fieldSelectRelationshipsSection,
  fieldInsuranceSelectRelationshipsSection,
  fieldPlanTypeSelectSection,
  fieldSelectStatesSection
} from '../field-helpers';
import validatorTypes from '@data-driven-forms/react-form-renderer/dist/cjs/validator-types';
import { FileUploadProps } from '../../../components/consent-modal/FileUpload';
const { caregiverFields, patientFields, insurancefields } = FIELD_NAMES;

const fileUploadProps: FileUploadProps = {
  disabled: false,
  showHelperText: true,
  dropzoneOptions: { maxFiles: 3, maxSize: 1000000 }
};
export const getPatientFormSchema = (schema: DBFormSchema): Schema => ({
  title: 'Add Patient Form',
  fields: [
    headerSection(schema?.personal_info_header?.label, schema?.personal_info_header, {
      className: 'add-patient-header'
    }),
    fieldTextSection(patientFields.firstName, schema?.[patientFields.firstName], {
      sizes: { md: 3 },
      validate: [
        {
          type: 'name',
          max: 20,
          label: 'First Name'
        }
      ],
      className: 'add-patient-text'
    }),
    fieldTextSection(patientFields.middleName, schema?.[patientFields.middleName], {
      sizes: { md: 3 },
      validate: [
        {
          type: 'name',
          max: 20,
          label: 'Middle Name'
        }
      ],
      className: 'add-patient-text'
    }),
    fieldTextSection(patientFields.lastName, schema?.[patientFields.lastName], {
      sizes: { md: 3 },
      validate: [
        {
          type: 'name',
          max: 20,
          label: 'Last Name'
        }
      ],
      className: 'add-patient-text'
    }),
    fieldSelectSection(patientFields.suffix, schema?.[patientFields.suffix], {
      sizes: { md: 2 },
      options: [
        {
          label: 'Jr.',
          value: 'Jr.'
        },
        {
          label: 'Sr.',
          value: 'Sr.'
        },
        {
          label: 'II',
          value: 'II'
        },
        {
          label: 'III',
          value: 'III'
        }
      ],
      className: 'add-patient-text'
    }),
    spacerSection(1, { sizes: { sm: 12 }, className: 'mb-4' }), // Adjust the class as per your framework
    fieldDateSection(patientFields.dob, schema?.[patientFields.dob], {
      sizes: { md: 3 },
      placeholder: 'MM/DD/YYYY'
    }),
    fieldRadioSection(patientFields.gender, schema?.[patientFields.gender], {
      sizes: { md: 3 },
      className: 'custom-class add-patient-text form-group',
      options: schema?.[patientFields.otherGender]
        ? [
            {
              value: 'F',
              label: 'Female'
            },
            {
              value: 'M',
              label: 'Male'
            },
            {
              value: 'O',
              label: 'Other'
            }
          ]
        : [
            {
              value: 'M',
              label: 'Male'
            },
            {
              value: 'F',
              label: 'Female'
            }
          ]
    }),
    fieldTextSection(patientFields.otherGender, schema?.[patientFields.otherGender], {
      sizes: { md: 3 },
      condition: {
        when: patientFields.gender,
        is: 'O'
      },
      className: 'add-patient-text'
    }),
    fieldSelectLanguagesSection(patientFields.preferredLanguage, schema?.[patientFields.preferredLanguage], {
      sizes: { md: 3 },
      className: 'select-language add-patient-text'
    }),
    fieldTextSection(patientFields.otherLanguage, schema?.[patientFields.otherLanguage], {
      sizes: { md: 2 },
      condition: {
        when: patientFields.preferredLanguage,
        is: 'Other'
      },
      className: 'add-patient-text'
    }),
    spacerSection(2, { sizes: { sm: 12 }, className: 'mb-4' }),
    headerSection(schema?.contact_header?.label, schema?.contact_header, { className: 'add-patient-header' }),
    fieldTextSection(patientFields.email, schema?.[patientFields.email], {
      sizes: { md: 3 },
      validate: [
        {
          type: validatorTypes.PATTERN,
          pattern:
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{1,}))$/,
          message: 'Email is invalid'
        }
      ],
      className: 'add-patient-text'
    }),
    fieldTextSection(patientFields.homeNumber, schema?.[patientFields.homeNumber], {
      sizes: { md: 3 },
      type: 'tel',
      validate: [
        {
          type: 'phone-conditional',
          compareToField: patientFields.mobileNumber
        }
      ],
      className: 'add-patient-text'
    }),
    fieldTextSection(patientFields.mobileNumber, schema?.[patientFields.mobileNumber], {
      sizes: { md: 3 },
      type: 'tel',
      validate: [
        {
          type: 'phone-conditional',
          compareToField: patientFields.homeNumber
        }
      ],
      className: 'add-patient-text'
    }),
    spacerSection(3, { sizes: { sm: 12 }, className: 'mb-4' }),
    fieldRadioSection(patientFields.messageOption, schema?.[patientFields.messageOption], {
      sizes: { md: 3 },
      className: 'custom-class add-patient-text form-group',
      options: [
        {
          value: 'email',
          label: 'Email'
        },
        {
          value: 'home',
          label: 'Home'
        },
        {
          value: 'mobile',
          label: 'Mobile'
        }
      ]
    }),
    spacerSection(3, { sizes: { sm: 12 }, className: 'mb-2' }),
    fieldTextSection(patientFields.address1, schema?.[patientFields.address1], {
      sizes: { md: 4 },
      validate: [
        {
          type: 'address'
        }
      ],
      className: 'add-patient-text'
    }),
    fieldTextSection(patientFields.address2, schema?.[patientFields.address2], {
      sizes: { md: 3 },
      validate: [
        {
          type: 'address'
        }
      ],
      className: 'add-patient-text'
    }),
    spacerSection(4, { sizes: { sm: 12 }, className: 'mb-4' }),
    fieldTextSection(patientFields.city, schema?.[patientFields.city], {
      sizes: { md: 3 },
      validate: [
        {
          type: 'city'
        }
      ],
      className: 'add-patient-text'
    }),
    fieldSelectStatesSection(patientFields.state, schema?.[patientFields.state], {
      sizes: { md: 2 },
      className: 'add-patient-text'
    }),
    fieldTextSection(patientFields.zip, schema?.[patientFields.zip], {
      sizes: { md: 2 },
      type: 'zip',
      validate: [
        {
          type: 'zip'
        }
      ],
      className: 'add-patient-text'
    }),
    spacerSection(5, { sizes: { sm: 12 }, className: 'mb-4' }),
    headerSection(schema?.insurance_information_header?.label, schema?.insurance_information_header, {
      className: 'add-patient-header'
    }),
    fieldRadioSection(patientFields.hasInsurance, schema?.[patientFields.hasInsurance], {
      sizes: { md: 6 },
      className: 'custom-class form-group patinet-caregiverText',
      options: [
        {
          value: 'yes',
          label: 'Yes'
        },
        {
          value: 'no',
          label: 'No'
        }
      ]
    }),
    spacerSection(6, { sizes: { sm: 12 }, ...hasInsuranceCondition }),
    fieldTextSection(
      insurancefields.insuranceMedicareBeneficiaryId,
      schema?.[insurancefields.insuranceMedicareBeneficiaryId],
      {
        sizes: { md: 4 },
        className: 'add-patient-text',
        ...hasInsuranceCondition
      }
    ),
    spacerSection(6, { sizes: { sm: 12 }, className: 'mb-4', ...hasInsuranceCondition }),
    headerSection(schema?.primary_information_header?.label, schema?.primary_information_header, {
      className: 'add-patient-sub-header',
      ...hasInsuranceCondition
    }),
    fieldTextSection(insurancefields.insuranceName, schema?.[insurancefields.insuranceName], {
      sizes: { md: 3 },
      className: 'add-patient-text',
      ...hasInsuranceCondition
    }),
    fieldTextSection(insurancefields.planName, schema?.[insurancefields.planName], {
      sizes: { md: 3 },
      className: 'add-patient-text',
      ...hasInsuranceCondition
    }),
    fieldTextSection(insurancefields.policyNumber, schema?.[insurancefields.policyNumber], {
      sizes: { md: 3 },
      className: 'add-patient-text',
      ...hasInsuranceCondition
    }),
    fieldPlanTypeSelectSection(insurancefields.planType, schema?.[insurancefields.planType], {
      sizes: { md: 3 },
      className: 'add-patient-text',
      ...hasInsuranceCondition
    }),
    spacerSection(6, { sizes: { sm: 12 }, className: 'mb-4', ...hasInsuranceCondition }),
    fieldTextSection(insurancefields.employerName, schema?.[insurancefields.employerName], {
      sizes: { md: 3 },
      className: 'add-patient-text',
      ...hasInsuranceCondition
    }),
    fieldTextSection(insurancefields.groupNumber, schema?.[insurancefields.groupNumber], {
      sizes: { md: 3 },
      className: 'add-patient-text',
      ...hasInsuranceCondition
    }),
    fieldTextSection(insurancefields.phoneNumber, schema?.[insurancefields.phoneNumber], {
      sizes: { md: 3 },
      className: 'add-patient-text',
      ...hasInsuranceCondition
    }),
    fieldInsuranceSelectRelationshipsSection(
      insurancefields.subscriberRelationship,
      schema?.[insurancefields.subscriberRelationship],
      {
        sizes: { md: 3 },
        className: 'add-patient-text',
        ...hasInsuranceCondition
      }
    ),
    spacerSection(6, { sizes: { sm: 12, className: 'mb-4', ...hasInsuranceCondition } }),
    headerSection(insurancefields.uploadInsuranceCardName, schema?.[insurancefields.uploadInsuranceCardName], {
      sizes: { md: 4 },
      ...hasInsuranceCondition
    }),
    fieldUploadSection(insurancefields.uploadAFile, schema?.[insurancefields.uploadAFile], {
      sizes: { md: 5 },
      className: 'add-patient-text',
      dropzoneOptions: { maxFiles: 3, maxSize: 1000000, accept: '.pdf' },
      showHelperText: true,
      showGenericFileName: true,
      ...hasInsuranceCondition
    }),
    spacerSection(6, { sizes: { sm: 12 }, ...hasInsuranceCondition }),
    fieldRadioSection(patientFields.hasSecondaryInsurance, schema?.[patientFields.hasSecondaryInsurance], {
      sizes: { md: 6 },
      className: 'custom-class form-group patinet-caregiverText',
      options: [
        {
          value: 'yes',
          label: 'Yes'
        },
        {
          value: 'no',
          label: 'No'
        }
      ],
      ...hasInsuranceCondition
    }),
    spacerSection(6, { sizes: { sm: 12 }, ...hasSecondaryInsuranceCondition }),
    fieldTextSection(
      insurancefields.secondaryInsuranceMedicareBeneficiaryId,
      schema?.[insurancefields.secondaryInsuranceMedicareBeneficiaryId],
      {
        sizes: { md: 4 },
        className: 'add-patient-text secondary-insurance',
        ...hasSecondaryInsuranceCondition
      }
    ),
    spacerSection(6, { sizes: { sm: 12 }, className: 'mb-4', ...hasSecondaryInsuranceCondition }),
    headerSection(schema?.secondary_insurance_header?.label, schema?.secondary_insurance_header, {
      className: 'add-patient-sub-header',
      ...hasSecondaryInsuranceCondition
    }),
    fieldTextSection(insurancefields.secondaryInsuranceName, schema?.[insurancefields.secondaryInsuranceName], {
      sizes: { md: 3 },
      className: 'add-patient-text',
      ...hasSecondaryInsuranceCondition
    }),
    fieldTextSection(insurancefields.secondaryPlanName, schema?.[insurancefields.secondaryPlanName], {
      sizes: { md: 3 },
      className: 'add-patient-text',
      ...hasSecondaryInsuranceCondition
    }),
    fieldTextSection(insurancefields.secondaryPolicyNumber, schema?.[insurancefields.secondaryPolicyNumber], {
      sizes: { md: 3 },
      className: 'add-patient-text',
      ...hasSecondaryInsuranceCondition
    }),
    fieldPlanTypeSelectSection(insurancefields.secondaryPlanType, schema?.[insurancefields.secondaryPlanType], {
      sizes: { md: 3 },
      className: 'add-patient-text',
      ...hasSecondaryInsuranceCondition
    }),
    spacerSection(6, { sizes: { sm: 12 }, className: 'mb-4', ...hasSecondaryInsuranceCondition }),
    fieldTextSection(insurancefields.secondaryEmployerName, schema?.[insurancefields.secondaryEmployerName], {
      sizes: { md: 3 },
      className: 'add-patient-text',
      ...hasSecondaryInsuranceCondition
    }),
    fieldTextSection(insurancefields.secondaryGroupNumber, schema?.[insurancefields.secondaryGroupNumber], {
      sizes: { md: 3 },
      className: 'add-patient-text',
      ...hasSecondaryInsuranceCondition
    }),
    fieldTextSection(insurancefields.secondaryPhoneNumber, schema?.[insurancefields.secondaryPhoneNumber], {
      sizes: { md: 3 },
      className: 'add-patient-text',
      ...hasSecondaryInsuranceCondition
    }),
    fieldInsuranceSelectRelationshipsSection(
      insurancefields.secondarySubscriberRelationship,
      schema?.[insurancefields.secondarySubscriberRelationship],
      {
        sizes: { md: 3 },
        className: 'add-patient-text',
        ...hasSecondaryInsuranceCondition
      }
    ),
    spacerSection(6, { sizes: { sm: 12, className: 'mb-4' }, ...hasSecondaryInsuranceCondition }),
    headerSection(
      insurancefields.secondaryUploadInsuranceCard,
      schema?.[insurancefields.secondaryUploadInsuranceCard],
      {
        sizes: { md: 4 },
        ...hasSecondaryInsuranceCondition
      }
    ),
    fieldUploadSection(insurancefields.secondaryUploadAFile, schema?.[insurancefields.secondaryUploadAFile], {
      sizes: { md: 5 },
      dropzoneOptions: { maxFiles: 3, maxSize: 1000000, accept: '.pdf' },
      showHelperText: true,
      showGenericFileName: true,
      ...hasSecondaryInsuranceCondition
    }),

    spacerSection(6, { sizes: { sm: 12 } }),
    headerSection(schema?.caregiver_information_header?.label, schema?.caregiver_information_header, {
      className: 'add-patient-header'
    }),
    fieldRadioSection(patientFields.hasCaregiver, schema?.[patientFields.hasCaregiver], {
      sizes: { md: 6 },
      className: 'custom-class form-group patinet-caregiverText',
      options: [
        {
          value: 'yes',
          label: 'Yes'
        },
        {
          value: 'no',
          label: 'No'
        }
      ]
    }),
    spacerSection(6, { sizes: { sm: 12 }, ...hasCaregiverCondition }),
    fieldTextSection(caregiverFields.firstName, schema?.[caregiverFields.firstName], {
      sizes: { md: 4 },
      className: 'add-patient-text',
      ...hasCaregiverCondition
    }),
    fieldTextSection(caregiverFields.middleName, schema?.[caregiverFields.middleName], {
      sizes: { md: 3 },
      className: 'add-patient-text',
      ...hasCaregiverCondition
    }),
    fieldTextSection(caregiverFields.lastName, schema?.[caregiverFields.lastName], {
      sizes: { md: 3 },
      className: 'add-patient-text',
      ...hasCaregiverCondition
    }),
    fieldSelectSection(caregiverFields.suffix, schema?.[caregiverFields.suffix], {
      sizes: { md: 2 },
      className: 'add-patient-text',
      options: [
        {
          label: 'Jr.',
          value: 'Jr.'
        },
        {
          label: 'Sr.',
          value: 'Sr.'
        },
        {
          label: 'II',
          value: 'II'
        },
        {
          label: 'III',
          value: 'III'
        }
      ],
      ...hasCaregiverCondition
    }),
    spacerSection(7, { sizes: { sm: 12 }, className: 'mb-4', ...hasCaregiverCondition }),
    fieldTextSection(caregiverFields.email, schema?.[caregiverFields.email], {
      sizes: { md: 3 },
      className: 'add-patient-text',
      validate: [
        {
          type: validatorTypes.PATTERN,
          pattern:
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{1,}))$/,
          message: 'Email is invalid'
        }
      ],
      ...hasCaregiverCondition
    }),
    fieldTextSection(caregiverFields.homePhone, schema?.[caregiverFields.homePhone], {
      sizes: { md: 3 },
      className: 'add-patient-text',
      type: 'tel',
      ...hasCaregiverCondition
    }),
    fieldTextSection(caregiverFields.mobilePhone, schema?.[caregiverFields.mobilePhone], {
      sizes: { md: 3 },
      className: 'add-patient-text',
      type: 'tel',
      ...hasCaregiverCondition
    }),
    //spacerSection(8, { sizes: { sm: 12 }, className: 'mb-4', ...hasCaregiverCondition }),
    fieldSelectRelationshipsSection(
      caregiverFields.patientRelationship,
      schema?.[caregiverFields.patientRelationship],
      {
        sizes: { md: 3 },
        className: 'add-patient-text',
        ...hasCaregiverCondition
      }
    ),
    spacerSection(9, { sizes: { sm: 12 }, ...hasCaregiverCondition }),
    fieldToggleSection(caregiverFields.isAuthorizedRepForPatient, schema?.[caregiverFields.isAuthorizedRepForPatient], {
      sizes: { md: 6 },
      className: 'text-dark',
      options: [
        {
          value: true,
          label: 'This caregiver is a legally authorized representative for this patient.'
        }
      ],
      ...hasCaregiverCondition
    }),
    spacerSection(10, { sizes: { sm: 12 }, ...hasCaregiverCondition })
  ].filter(Boolean)
});

const hasCaregiverCondition = {
  condition: {
    when: patientFields.hasCaregiver,
    is: (value: string) => value === 'yes' // Check if the radio button value is 'yes'
  }
};
const hasInsuranceCondition = {
  condition: {
    when: patientFields.hasInsurance,
    is: (value: string) => value === 'yes' // Check if the radio button value is 'yes'
  }
};

const hasSecondaryInsuranceCondition = {
  condition: {
    and: [
      { when: patientFields.hasInsurance, is: 'yes' }, // Primary insurance must be "Yes"
      { when: patientFields.hasSecondaryInsurance, is: 'yes' } // Secondary insurance must be "Yes"
    ]
  }
};

// -----===[test schema]===-----
export const addPatientFormSchema: DBFormSchema = {
  personal_info_header: { label: 'personal information' },
  patient_first_name: { label: 'first name', required: true },
  patient_middle_name: { label: 'middle name' },
  patient_last_name: { label: 'last name', required: true },
  patient_dob: { label: 'date of birth', required: true },
  patient_gender: { label: 'gender', required: true },
  patient_preferred_language: { label: 'preferred language', required: true },
  patient_other_language: { label: 'specify "other" language', required: true },
  contact_header: { label: 'contact and address information' },
  patient_email: { label: 'email address' },
  patient_home_number: { label: 'home phone number', placeholder: 'optional' },
  patient_mobile_number: { label: 'mobile number', placeholder: 'optional' },
  patient_message_option: { label: 'okay to leave message' },
  patient_voicemail_option: { label: 'voicemail authorization' },
  patient_address_1: { label: 'address 1', required: true },
  patient_address_2: { label: 'address 2', placeholder: 'optional' },
  patient_zip: { label: 'zip code', required: true },
  patient_city: { label: 'city', required: true },
  patient_state: { label: 'state', required: true },
  caregiver_information_header: { label: 'caregiver information' },
  insurance_information_header: { label: 'insurance information' },
  patient_has_caregiver: {},
  caregiver_first_name: { label: 'first name', required: true },
  caregiver_middle_name: { label: 'middle name' },
  caregiver_last_name: { label: 'last name', required: true },
  caregiver_email: { label: 'email address' },
  caregiver_home_phone: { label: 'home phone number' },
  caregiver_mobile_phone: { label: 'mobile number' },
  caregiver_patient_relationship: { label: 'relationship to patient', required: true },
  secondary_insurance_upload_insurance_card: { label: 'Insurance Card(s) Upload:' },
  caregiver_is_authorized_representative: {}
};
